import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { User } from '@app/core/model/user.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { CommonModule } from '@angular/common'
import { SharedModule } from '@app/shared/shared.module'
import { ScopeUiTableComponent } from '@app/shared/components/ui-components/scope-ui-table/scope-ui-table.component'
import { NgxMaskDirective } from 'ngx-mask'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { plainToInstance } from 'class-transformer'
import { Store } from '@ngrx/store'
import { trackById } from '@shared/utils/utils'
import { Money } from '@app/features/scope-overview/model/money.model'
import { ScopeUiDatepickerComponent } from '@shared/components/ui-components/scope-ui-datepicker/scope-ui-datepicker.component'
import { MatMenuTrigger } from '@angular/material/menu'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { CdnConfig } from '@core/model/cdn-config.model'
import { ExchangeRateService } from '@shared/services/exchange-rate.service'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import { ScopeElementsModule } from '@app/shared/components/scope-elements/scope-elements.module'
import {
  ScopeElementsConfig,
  UpdateFeeItemPayload,
  UpdateRolePayload,
  UpdateTpcPayload,
} from '@app/shared/components/scope-elements/models'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { isPreferenceActive } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { AuthService } from '@app/core/service/auth.service'

@Component({
  selector: 'scope-section-row',
  imports: [
    CommonModule,
    SharedModule,
    ScopeElementsModule,
    ScopeUiTableComponent,
    NgxMaskDirective,
    ScopeUiInputComponent,
    ScopeUiDatepickerComponent,
  ],
  templateUrl: './section-row.component.html',
  styleUrls: ['./section-row.component.scss'],
  standalone: true,
})
export class SectionRowComponent {
  @Input() tableConfig?: ScopeElementsConfig
  @Input() section!: ScopeSection
  @Input() feeMenuOptions!: MenuOptions[]
  @Input() deliverableMenuOptions?: MenuOptions[]
  @Input() componentMenuOptions?: MenuOptions[]
  @Input() currentRatecard: RatecardVersion
  @Input() sectionSelectedStates: Record<number, boolean>
  @Input() cdnConfig?: CdnConfig

  @Output() onFetchDeliverable = new EventEmitter<number>()
  @Output() onUpdateDeliverable = new EventEmitter<Deliverable>()
  @Output() onUpdateComponent = new EventEmitter<ScopeComponent>()
  @Output() onAcceptDeliverable = new EventEmitter<Deliverable>()
  @Output() onRejectDeliverable = new EventEmitter<Deliverable>()
  @Output() onDropDeliverable = new EventEmitter<{ event: CdkDragDrop<any>; section: ScopeSection | null }>()

  @Output() onAddComponentFee = new EventEmitter<ScopeComponent>()
  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onOverrideComponentQuantity = new EventEmitter<ScopeComponent>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()
  @Output() onDeleteSection = new EventEmitter<ScopeSection>()
  @Output() onAddSectionFee = new EventEmitter<ScopeSection>()
  @Output() onUpdateFeeItem = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onUpdateDeliverableTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onUpdateComponentTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onUpdateDeliverableSectionTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onReorderDeliverableTpcs = new EventEmitter<any>()
  @Output() onReorderComponentTpcs = new EventEmitter<any>()
  @Output() onReorderDeliverableSectionTpcs = new EventEmitter<any>()
  @Output() onMoveComponent = new EventEmitter<any>()
  @Output() onMoveComponentOutsideDeliverable = new EventEmitter<ScopeComponent>()

  @ViewChild(MatMenuTrigger) startDateMenuTrigger: MatMenuTrigger
  @ViewChild(MatMenuTrigger) endDateMenuTrigger: MatMenuTrigger

  editSectionName: number

  get currentUser(): User {
    return this.authService.loggedInUser
  }

  constructor(
    private store: Store,
    private authService: AuthService,
    protected scopeElementsService: ScopeElementsService,
    protected exchangeRateService: ExchangeRateService,
    protected scopeApprovalFlowService: ApprovalFlowService,
    protected scopeTabService: ScopeTabService
  ) {}

  updateSectionFee(feeItem: FeeItemInstance) {
    this.onUpdateFeeItem.emit({ feeItem, section: this.section })
  }

  updateSectionDescription(description: string, section: ScopeSection) {
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...section, description }),
      })
    )
  }

  updateSectionNote(internalNote: string, section: ScopeSection) {
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...section, internalNote }),
      })
    )
  }

  updateSectionName(name: any, section: ScopeSection) {
    this.editSectionName = null
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...section, name }),
      })
    )
  }

  updateStartDate(startDate: Date) {
    this.startDateMenuTrigger.closeMenu()
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...this.section, startDate }),
      })
    )
  }

  updateEndDate(endDate: Date) {
    this.endDateMenuTrigger.closeMenu()
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...this.section, endDate }),
      })
    )
  }

  getProfitOfSection(section: ScopeSection) {
    if (section.totalProfit) {
      return section.totalProfit
    }
    let result = new Money(0, this.tableConfig?.scopeVersion.identity.rateCard.currencyCode)
    section.deliverables.forEach((deliverable) => {
      result = result.add(deliverable.getTotalProfit())
    })
    return result
  }

  protected readonly trackById = trackById
  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly isPreferenceActive = isPreferenceActive
}
